import type {
  PublicAccessType,
  SpotDefinition,
  SpotId,
  ToiletFacility,
} from "./types";

export const DEFAULT_SPOT_ID: SpotId = "ariake";

export const SPOT_OPTIONS = [
  {
    id: "ariake",
    municipalityCode: "13108",
    name: "江東区・有明",
    center: { latitude: 35.6328, longitude: 139.7946 },
    radiusMeters: 3000,
  },
  {
    id: "tokyo-dome",
    municipalityCode: "13105",
    name: "文京区・東京ドーム周辺",
    center: { latitude: 35.7056, longitude: 139.7519 },
    radiusMeters: 1200,
  },
  {
    id: "shibuya-station",
    municipalityCode: "13113",
    name: "渋谷区・渋谷駅周辺",
    center: { latitude: 35.658, longitude: 139.7016 },
    radiusMeters: 1300,
  },
  {
    id: "ikebukuro-station",
    municipalityCode: "13116",
    name: "豊島区・池袋駅周辺",
    center: { latitude: 35.7295, longitude: 139.7109 },
    radiusMeters: 1300,
  },
] as const satisfies readonly SpotDefinition[];

const spotById = new Map<SpotId, SpotDefinition>(
  SPOT_OPTIONS.map((spot) => [spot.id, spot]),
);

export function isSpotId(value: string): value is SpotId {
  return spotById.has(value as SpotId);
}

export function getSpotDefinition(spotId: SpotId): SpotDefinition {
  return spotById.get(spotId) ?? spotById.get(DEFAULT_SPOT_ID)!;
}

type SpotToiletInput = {
  id: string;
  officialFacilityId: string;
  name: string;
  nameEn: string;
  address: string;
  latitude: number;
  longitude: number;
  publicAccess: PublicAccessType;
  openingLabel: string;
  openingHours?: [string, string] | null;
  openingDays?: number[];
  openingNote?: string;
  sourceId?:
    | "tokyo-barrier-free-wc"
    | "toshima-public-toilets"
    | "tokyo-railway-toilets"
    | "tokyo-daredemo-facilities";
  sourceUpdatedAt?: string;
  facilityType?: string;
  operatorName?: string;
  roomCount?: number | null;
  wheelchair?: boolean | null;
  ostomate?: boolean | null;
  babyChanging?: boolean | null;
  childSeat?: boolean | null;
  genderNeutral?: boolean | null;
};

const spotToilet = ({
  id,
  officialFacilityId,
  name,
  nameEn,
  address,
  latitude,
  longitude,
  publicAccess,
  openingLabel,
  openingHours = ["00:00", "23:59"],
  openingDays = [0, 1, 2, 3, 4, 5, 6],
  openingNote,
  sourceId = "tokyo-barrier-free-wc",
  sourceUpdatedAt = "2025-01-01T00:00:00+09:00",
  facilityType,
  operatorName = "施設管理者",
  roomCount = null,
  wheelchair = null,
  ostomate = null,
  babyChanging = null,
  childSeat = null,
  genderNeutral = null,
}: SpotToiletInput): ToiletFacility => ({
  id,
  officialFacilityId,
  name,
  nameEn,
  address,
  coordinates: { latitude, longitude },
  facilityType:
    facilityType ?? (publicAccess === "always_public" ? "公衆トイレ" : "公共施設"),
  operatorName,
  publicAccess,
  openingLabel,
  openingPeriods: openingHours
    ? [{ days: openingDays, open: openingHours[0], close: openingHours[1] }]
    : [],
  openingNote,
  features: {
    wheelchair,
    ostomate,
    baby_changing: babyChanging,
    child_seat: childSeat,
    gender_neutral: genderNeutral,
  },
  roomCount,
  sourceIds: [sourceId],
  sourceUpdatedAt,
});

export const SPOT_TOILETS: Readonly<Record<Exclude<SpotId, "ariake">, ToiletFacility[]>> = {
  "tokyo-dome": [
    spotToilet({
      id: "d7200002-0000-4000-8000-000000000001",
      officialFacilityId: "tokyo-bf-13105-99-1-1",
      name: "文京シビックセンター",
      nameEn: "Bunkyo Civic Center",
      address: "東京都文京区春日一丁目16番21号",
      latitude: 35.708164,
      longitude: 139.752379,
      publicAccess: "business_hours_only",
      openingLabel: "7:30〜22:00（フロアにより異なる）",
      openingHours: ["07:30", "22:00"],
      openingNote: "年末年始・設備点検等の休館時は利用できません。",
      operatorName: "文京区",
      wheelchair: true,
      ostomate: true,
      babyChanging: true,
      childSeat: true,
      genderNeutral: true,
    }),
    spotToilet({
      id: "d7200002-0000-4000-8000-000000000002",
      officialFacilityId: "tokyo-bf-13105-99-7-15-1",
      name: "礫川公園便所",
      nameEn: "Rekisen Park Public Toilet",
      address: "東京都文京区春日一丁目15番",
      latitude: 35.708136,
      longitude: 139.751389,
      publicAccess: "always_public",
      openingLabel: "24時間",
      operatorName: "文京区",
      wheelchair: true,
      babyChanging: true,
      childSeat: true,
      genderNeutral: true,
    }),
    spotToilet({
      id: "d7200002-0000-4000-8000-000000000003",
      officialFacilityId: "tokyo-bf-13000-15-7-71-3",
      name: "小石川後楽園 東門だれでもトイレ",
      nameEn: "Koishikawa Korakuen East Gate Accessible Toilet",
      address: "東京都文京区後楽一丁目6番6号",
      latitude: 35.704683,
      longitude: 139.748423,
      publicAccess: "facility_users_only",
      openingLabel: "9:00〜17:00",
      openingHours: ["09:00", "17:00"],
      openingNote: "庭園入園者のみ利用できます。12月29日〜1月1日は閉園です。",
      operatorName: "東京都",
      wheelchair: true,
      ostomate: true,
      babyChanging: true,
      childSeat: true,
      genderNeutral: true,
    }),
    spotToilet({
      id: "d7200002-0000-4000-8000-000000000004",
      officialFacilityId: "tokyo-rail-10-41",
      name: "東京地下鉄 後楽園駅",
      nameEn: "Tokyo Metro Korakuen Station",
      address: "東京都文京区春日1丁目2番3号",
      latitude: 35.708087,
      longitude: 139.751633,
      facilityType: "鉄道駅",
      operatorName: "東京地下鉄",
      publicAccess: "facility_users_only",
      openingLabel: "始発〜終車（詳細は駅で確認）",
      openingHours: null,
      openingNote: "改札内外・利用時間は駅で確認してください。",
      sourceId: "tokyo-railway-toilets",
      sourceUpdatedAt: "2024-01-01T00:00:00+09:00",
      roomCount: 3,
      wheelchair: true,
      ostomate: true,
      babyChanging: true,
      childSeat: true,
      genderNeutral: true,
    }),
  ],
  "shibuya-station": [
    spotToilet({
      id: "d7200003-0000-4000-8000-000000000001",
      officialFacilityId: "tokyo-bf-13113-99-8-10-1",
      name: "渋谷駅東口公衆便所",
      nameEn: "Shibuya Station East Exit Public Toilet",
      address: "東京都渋谷区渋谷二丁目23番14号",
      latitude: 35.657942,
      longitude: 139.703104,
      publicAccess: "always_public",
      openingLabel: "4:35〜翌1:00",
      openingHours: ["04:35", "01:00"],
      operatorName: "渋谷区",
      wheelchair: true,
      ostomate: true,
      babyChanging: true,
      childSeat: true,
      genderNeutral: true,
    }),
    spotToilet({
      id: "d7200003-0000-4000-8000-000000000002",
      officialFacilityId: "tokyo-bf-13113-99-7-5-1",
      name: "神宮通公園 公園便所",
      nameEn: "Jingu-dori Park Public Toilet",
      address: "東京都渋谷区神宮前六丁目22番8号",
      latitude: 35.664193,
      longitude: 139.702053,
      publicAccess: "always_public",
      openingLabel: "24時間",
      operatorName: "渋谷区",
      wheelchair: true,
      babyChanging: true,
      childSeat: true,
      genderNeutral: true,
    }),
    spotToilet({
      id: "d7200003-0000-4000-8000-000000000003",
      officialFacilityId: "tokyo-bf-13113-99-1-5-1",
      name: "渋谷区役所本庁舎 1階だれでもトイレ",
      nameEn: "Shibuya City Office Accessible Toilet",
      address: "東京都渋谷区宇田川町1番1号",
      latitude: 35.663797,
      longitude: 139.697826,
      publicAccess: "business_hours_only",
      openingLabel: "平日 8:30〜17:15",
      openingHours: ["08:30", "17:15"],
      openingDays: [1, 2, 3, 4, 5],
      openingNote: "土日祝日・閉庁日は利用できません。",
      operatorName: "渋谷区",
      wheelchair: true,
      ostomate: true,
      babyChanging: true,
      childSeat: true,
      genderNeutral: true,
    }),
    spotToilet({
      id: "d7200003-0000-4000-8000-000000000004",
      officialFacilityId: "tokyo-rail-14-56",
      name: "東日本旅客鉄道 渋谷駅",
      nameEn: "JR East Shibuya Station",
      address: "東京都渋谷区道玄坂1丁目",
      latitude: 35.658005,
      longitude: 139.701802,
      facilityType: "鉄道駅",
      operatorName: "東日本旅客鉄道",
      publicAccess: "facility_users_only",
      openingLabel: "始発〜終車（詳細は駅で確認）",
      openingHours: null,
      openingNote: "改札内外・利用時間は駅で確認してください。",
      sourceId: "tokyo-railway-toilets",
      sourceUpdatedAt: "2026-01-01T00:00:00+09:00",
      roomCount: 3,
      wheelchair: true,
      ostomate: true,
      babyChanging: true,
      childSeat: true,
      genderNeutral: true,
    }),
    spotToilet({
      id: "d7200003-0000-4000-8000-000000000005",
      officialFacilityId: "daredemo-shopping-shibuya-hikarie",
      name: "渋谷ヒカリエ",
      nameEn: "Shibuya Hikarie",
      address: "東京都渋谷区渋谷2丁目21番1号",
      latitude: 35.65900944,
      longitude: 139.7035447,
      facilityType: "大型商業施設",
      operatorName: "施設運営者",
      publicAccess: "facility_users_only",
      openingLabel: "営業時間・利用条件は施設で確認",
      openingHours: null,
      openingNote: "施設利用者向けです。休館・店舗営業時間・入館条件を確認してください。",
      sourceId: "tokyo-daredemo-facilities",
      sourceUpdatedAt: "2022-02-07T00:00:00+09:00",
      roomCount: 11,
      wheelchair: true,
      ostomate: true,
      babyChanging: true,
      genderNeutral: true,
    }),
  ],
  "ikebukuro-station": [
    spotToilet({
      id: "d7200004-0000-4000-8000-000000000001",
      officialFacilityId: "131164-5",
      name: "池袋駅北口前公衆便所",
      nameEn: "Ikebukuro Station North Exit Public Toilet",
      address: "東京都豊島区西池袋一丁目1番40号",
      latitude: 35.73166667,
      longitude: 139.7113889,
      publicAccess: "always_public",
      openingLabel: "24時間（多機能トイレは夜間利用制限）",
      openingNote:
        "多機能トイレは夜間利用できません（カード所有者を除く）。利用可能時間は現地で確認してください。",
      sourceId: "toshima-public-toilets",
      sourceUpdatedAt: "2026-01-15T00:00:00+09:00",
      operatorName: "豊島区",
      wheelchair: true,
      babyChanging: true,
    }),
    spotToilet({
      id: "d7200004-0000-4000-8000-000000000002",
      officialFacilityId: "tokyo-bf-13000-6-1-10-1",
      name: "豊島合同庁舎 1階車椅子使用者対応トイレ",
      nameEn: "Toshima Joint Government Building Accessible Toilet",
      address: "東京都豊島区西池袋一丁目17番1号",
      latitude: 35.73095,
      longitude: 139.708405,
      publicAccess: "business_hours_only",
      openingLabel: "平日 8:30〜17:00",
      openingHours: ["08:30", "17:00"],
      openingDays: [1, 2, 3, 4, 5],
      openingNote: "閉庁日・施設運用により利用できない場合があります。",
      operatorName: "東京都",
      wheelchair: true,
      genderNeutral: true,
    }),
    spotToilet({
      id: "d7200004-0000-4000-8000-000000000003",
      officialFacilityId: "tokyo-bf-13000-7-5-6-10",
      name: "東京芸術劇場 地下2階バリアフリートイレ",
      nameEn: "Tokyo Metropolitan Theatre Accessible Toilet",
      address: "東京都豊島区西池袋一丁目8番1号",
      latitude: 35.72987,
      longitude: 139.7082,
      publicAccess: "facility_users_only",
      openingLabel: "7:00〜24:00",
      openingHours: ["07:00", "24:00"],
      openingNote: "休館日や施設運用により利用できない場合があります。",
      operatorName: "公益財団法人東京都歴史文化財団",
      wheelchair: true,
      babyChanging: true,
      genderNeutral: true,
    }),
    spotToilet({
      id: "d7200004-0000-4000-8000-000000000004",
      officialFacilityId: "tokyo-bf-13116-99-10-9-1",
      name: "としま区民センター 1階多目的トイレ",
      nameEn: "Toshima Civic Center Accessible Toilet",
      address: "東京都豊島区東池袋一丁目20番10号",
      latitude: 35.731419,
      longitude: 139.715613,
      publicAccess: "business_hours_only",
      openingLabel: "9:00〜22:00",
      openingHours: ["09:00", "22:00"],
      openingNote: "休館日や施設運用により利用できない場合があります。",
      operatorName: "豊島区",
      wheelchair: true,
      ostomate: true,
      babyChanging: true,
      childSeat: true,
      genderNeutral: true,
    }),
    spotToilet({
      id: "d7200004-0000-4000-8000-000000000005",
      officialFacilityId: "tokyo-rail-10-1",
      name: "東京地下鉄 池袋駅",
      nameEn: "Tokyo Metro Ikebukuro Station",
      address: "東京都豊島区西池袋3丁目28番14号",
      latitude: 35.730186,
      longitude: 139.71041,
      facilityType: "鉄道駅",
      operatorName: "東京地下鉄",
      publicAccess: "facility_users_only",
      openingLabel: "始発〜終車（詳細は駅で確認）",
      openingHours: null,
      openingNote: "改札内外・利用時間は駅で確認してください。",
      sourceId: "tokyo-railway-toilets",
      sourceUpdatedAt: "2024-01-01T00:00:00+09:00",
      roomCount: 3,
      wheelchair: true,
      ostomate: true,
      babyChanging: true,
      childSeat: true,
      genderNeutral: true,
    }),
    spotToilet({
      id: "d7200004-0000-4000-8000-000000000006",
      officialFacilityId: "daredemo-shopping-ikebukuro-parco",
      name: "池袋パルコ 本館",
      nameEn: "Ikebukuro PARCO Main Building",
      address: "東京都豊島区南池袋1丁目28番2号",
      latitude: 35.730954,
      longitude: 139.712462,
      facilityType: "大型商業施設",
      operatorName: "施設運営者",
      publicAccess: "facility_users_only",
      openingLabel: "営業時間・利用条件は施設で確認",
      openingHours: null,
      openingNote: "施設利用者向けです。休館・店舗営業時間・入館条件を確認してください。",
      sourceId: "tokyo-daredemo-facilities",
      sourceUpdatedAt: "2020-03-10T00:00:00+09:00",
      roomCount: 2,
      wheelchair: true,
      ostomate: true,
      babyChanging: false,
      genderNeutral: true,
    }),
  ],
};
